import React, { useState, useRef } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { PartnerCategory } from "src/store/services/partnerApi";
import { useOnClickOutside } from "src/hooks/useOnClickOutside";

const DefaultCategoryIcon = () => (
	<svg width='60' height='60' viewBox='0 0 60 60' fill='none'>
		<circle
			cx='30'
			cy='30'
			r='28'
			fill='#f5f5f5'
			stroke='#e0e0e0'
			strokeWidth='2'
		/>
		<circle cx='30' cy='30' r='12' fill='#9e9e9e' />
	</svg>
);

interface CategoryCardProps {
	category: PartnerCategory;
	onEdit: (category: PartnerCategory) => void;
	onDelete: (category: PartnerCategory) => void;
	onView: (category: PartnerCategory) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
	category,
	onEdit,
	onDelete,
	onView,
}) => {
	const [imageError, setImageError] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	const hasValidImage =
		category?.category_image_url &&
		category.category_image_url !== "https://local" &&
		!imageError;

	useOnClickOutside(menuRef, () => setIsMenuOpen(false));

	if (!category || !category.category_name) {
		return null;
	}

	const handleMenuClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setIsMenuOpen(!isMenuOpen);
	};

	const handleAction = (
		e: React.MouseEvent,
		action: (category: PartnerCategory) => void
	) => {
		e.stopPropagation();
		action(category);
		setIsMenuOpen(false);
	};

	return (
		<div className='bg-white rounded-2xl p-6 shadow-sm h-64 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer relative' style={{border: '1.33px solid #0000001A'}}>
			{/* Menu Button */}
			<div className='absolute top-4 right-4' ref={menuRef}>
				<button
					onClick={handleMenuClick}
					className='p-1 rounded-full hover:bg-gray-100 transition-colors'
					aria-label='More options'
				>
					<MoreVertIcon className='text-gray-600' fontSize='small' />
				</button>

				{/* Dropdown Menu */}
				{isMenuOpen && (
					<div className='absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10'>
						<button
							onClick={(e) => handleAction(e, onView)}
							className='w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg'
						>
							<VisibilityIcon fontSize='small' />
							<span>View</span>
						</button>
						<button
							onClick={(e) => handleAction(e, onEdit)}
							className='w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50'
						>
							<EditIcon fontSize='small' />
							<span>Edit</span>
						</button>
						<button
							onClick={(e) => handleAction(e, onDelete)}
							className='w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 last:rounded-b-lg'
						>
							<DeleteIcon fontSize='small' />
							<span>Delete</span>
						</button>
					</div>
				)}
			</div>

			<div onClick={(e) => handleAction(e, onView)}>
				<div className='flex justify-center items-center mb-2 h-28'>
					{hasValidImage ? (
						<img
							src={category.category_image_url}
							alt={category.category_name || "Category"}
							className='h-28 w-28 object-contain'
							onError={() => setImageError(true)}
						/>
					) : (
						<DefaultCategoryIcon />
					)}
				</div>
				<div className='text-lg font-semibold text-[#3a3541] text-center'  style={{'fontFamily':'Blinker'}}>
					{category.category_name || "Unnamed Category"}
				</div>
				{category.category_description && (
					<div className='text-base text-black p-3 pt-0 rounded-xl text-center line-clamp-2' style={{'fontFamily':'Blinker'}}>
						{category.count} Partner{category.count <= 1 ? "" : "s"}
					</div>
				)}
			</div>
		</div>
	);
};

export default CategoryCard;

